import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";
import { captureAttribution } from "@/lib/attribution";
import { ScrollMotion } from "@/components/layout/ScrollMotion";
import { NotFoundPage } from "@/components/layout/NotFoundPage";

import appCss from "../styles.css?url";
import sans200Woff2 from "@fontsource/ibm-plex-sans/files/ibm-plex-sans-latin-200-normal.woff2?url";
import sans300Woff2 from "@fontsource/ibm-plex-sans/files/ibm-plex-sans-latin-300-normal.woff2?url";
import sans400Woff2 from "@fontsource/ibm-plex-sans/files/ibm-plex-sans-latin-400-normal.woff2?url";
import mono500Woff2 from "@fontsource/ibm-plex-mono/files/ibm-plex-mono-latin-500-normal.woff2?url";
import logoBleu from "../assets/logo-bleu.png.asset.json";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { ORG_ID, PERSON_ID, POSTAL_ADDRESS, SITE, abs } from "@/content/siteConfig";

function ErrorComponent({ error, reset }: ErrorComponentProps) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div
      className="flex min-h-screen items-center justify-center"
      style={{ background: "#fbfaf6", padding: "0 24px" }}
    >
      <div className="max-w-md text-center">
        <h1
          style={{
            fontFamily: '"IBM Plex Sans", "IBM Plex Sans Fallback", sans-serif',
            fontWeight: 300,
            fontSize: 28,
            color: "#002FA7",
            margin: 0,
          }}
        >
          This page didn&apos;t load
        </h1>
        <p className="mt-3" style={{ color: "rgba(0,47,167,0.74)" }}>
          Something went wrong on our end. Try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="mono"
            style={{
              padding: "10px 18px",
              background: "#8a6f3f",
              color: "#fff",
              border: 0,
              borderRadius: 4,
              fontSize: 11.5,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              cursor: "pointer",
            }}
          >
            Try again
          </button>
          <Link
            to="/"
            className="mono"
            style={{
              padding: "10px 18px",
              border: "1px solid rgba(0,47,167,0.26)",
              color: "#002FA7",
              borderRadius: 4,
              fontSize: 11.5,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  loader: async () => {
    const { listRecentPostLinks } = await import("@/lib/blogPosts");
    return { recentPosts: (await listRecentPostLinks(5)) ?? [] };
  },
  head: ({ matches }) => ({
    meta: [
      ...(matches.some((match) => match.status === "notFound" || match._notFound)
        ? [{ title: "Page not found | Berk Hospitality Group" }, { name: "robots", content: "noindex" }]
        : []),
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "theme-color", content: "#FBFAF6" },
      { name: "author", content: "Berk Hospitality Group" },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Berk Hospitality Group" },
      { name: "facebook-domain-verification", content: "bdpfuczclegy7e1ytn9t2vi7gbn1g4" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32.png" },
      ...[sans200Woff2, sans300Woff2, sans400Woff2, mono500Woff2].map((href) => ({
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href,
        crossOrigin: "anonymous" as const,
      })),
      { rel: "stylesheet", href: appCss },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "@id": ORG_ID,
          name: SITE.name,
          slogan: "Ads, SEO and AI for home services companies",
          url: SITE.origin,
          email: SITE.email,
          telephone: SITE.phoneE164,
          description:
            "Berk Hospitality Group is a Scottsdale, Arizona marketing agency for home services companies: Google Ads, Meta Ads, Local Services Ads and YouTube, SEO and AI search, and AI automation including an AI receptionist, workflow automation, a company brain and custom software. Also runs paid media for local brands and online stores. Certified Google Partner and Meta Business Partner.",
          knowsAbout: [
            "Google Ads management",
            "Meta Ads management",
            "Local Services Ads",
            "Performance Max",
            "Meta Advantage+ Shopping campaigns",
            "Google local campaigns and store visits",
            "conversion tracking",
            "server-side tagging",
            "Google Tag Manager",
            "GA4",
            "offline conversion imports",
            "ecommerce and DTC paid media",
            "home services lead generation",
            "local and multi-location advertising",
            "SEO",
            "AI search optimization",
            "AI receptionist",
            "workflow automation",
            "custom software",
            "company brain",
          ],
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: "Services",
            itemListElement: [
              ["Google Ads", "/services/google-ads"],
              ["Meta Ads", "/services/meta-ads"],
              ["Local Services Ads", "/services/local-services-ads"],
              ["YouTube Ads", "/services/youtube-ads"],
              ["SEO & AI Search", "/services/seo-ai-search"],
              ["AI Receptionist", "/ai-automation/receptionist"],
              ["Workflow Automation", "/ai-automation/workflows"],
              ["Company Brain", "/ai-automation/company-brain"],
              ["Custom Software", "/ai-automation/custom-software"],
            ].map(([name, path]) => ({
              "@type": "Offer",
              itemOffered: { "@type": "Service", name, url: abs(path) },
            })),
          },
          logo: {
            "@type": "ImageObject",
            url: abs(logoBleu.url),
          },
          image: abs(logoBleu.url),
          foundingDate: "2021",
          areaServed: [
            { "@type": "Place", name: "Phoenix metro area" },
            { "@type": "State", name: "Arizona" },
            { "@type": "City", name: "Scottsdale", containedInPlace: { "@type": "State", name: "Arizona" } },
            { "@type": "City", name: "Phoenix", containedInPlace: { "@type": "State", name: "Arizona" } },
            { "@type": "City", name: "Mesa", containedInPlace: { "@type": "State", name: "Arizona" } },
            { "@type": "City", name: "Tempe", containedInPlace: { "@type": "State", name: "Arizona" } },
            { "@type": "City", name: "Chandler", containedInPlace: { "@type": "State", name: "Arizona" } },
            { "@type": "City", name: "Gilbert", containedInPlace: { "@type": "State", name: "Arizona" } },
          ],
          founder: { "@id": PERSON_ID },
          sameAs: [
            SITE.social.linkedinCompany,
            SITE.social.instagram,
            SITE.social.facebook,
            SITE.social.google,
            "https://www.designrush.com/agency/profile/berk-hospitality-group",
            "https://www.google.com/maps/place/?q=place_id:ChIJGbP_O-dkQ2kRSbWesmAhMMc",
            "https://www.bbb.org/us/az/scottsdale/profile/advertising/berk-hospitality-group-llc-1126-1000171712",
            "https://www.youtube.com/@berkhospitality",
            "https://x.com/BerkHospitality",
            "https://www.yelp.com/biz/berk-hospitality-group-scottsdale",
            "https://www.crunchbase.com/organization/berk-hospitality-group",
            "https://www.trustpilot.com/review/berkhospitality.com",
            "https://www.wikidata.org/wiki/Q141952441",
          ],
          address: POSTAL_ADDRESS,
          hasCredential: [
            {
              "@type": "EducationalOccupationalCredential",
              credentialCategory: "certification",
              name: "Google Partner",
              recognizedBy: { "@type": "Organization", name: "Google", url: "https://www.google.com" },
              url: "https://partnersdirectory.withgoogle.com/partners/9126630442",
            },
            {
              "@type": "EducationalOccupationalCredential",
              credentialCategory: "certification",
              name: "Meta Business Partner",
              recognizedBy: { "@type": "Organization", name: "Meta", url: "https://www.meta.com" },
            },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Person",
          "@id": PERSON_ID,
          name: "Jett Berk",
          jobTitle: "Founder & CEO",
          worksFor: { "@id": ORG_ID },
          url: abs("/about"),
          sameAs: [SITE.social.linkedinFounder, "https://x.com/jettbhg"],
          description:
            "Founder of Berk Hospitality Group, a Scottsdale marketing agency for home services companies. Runs Google and Meta ads, SEO and AI search, and builds AI receptionists, automations and custom software. Runs the agency on a company brain he built.",
          knowsAbout: [
            "Google Ads",
            "Local Services Ads",
            "Meta Ads",
            "SEO",
            "AI search",
            "AI automation",
            "Custom software",
            "Marketing for home services",
          ],
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundPage,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <script
          dangerouslySetInnerHTML={{
            __html:
              "window.dataLayer=window.dataLayer||[];(function(h){var isProd=h==='berkhospitality.com'||h.endsWith('.berkhospitality.com');if(!isProd)return;(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','GTM-KQMQ23JJ');})(window.location.hostname);",
          }}
        />
        <HeadContent />
      </head>
      <body>
        <noscript
          dangerouslySetInnerHTML={{
            __html:
              '<iframe src="https://www.googletagmanager.com/ns.html?id=GTM-KQMQ23JJ" height="0" width="0" style="display:none;visibility:hidden"></iframe>',
          }}
        />
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  useEffect(() => {
    try {
      captureAttribution();
    } catch {
      /* best-effort */
    }
  }, []);
  return (
    <QueryClientProvider client={queryClient}>
      <Outlet />
      <ScrollMotion />
    </QueryClientProvider>
  );
}
